'use client';

import { useMemo, useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import type { BulkPayload } from '@/types/bulk-action';
import { getSortingStateParser } from '@/lib/parsers';
import {
  Download,
  FileText,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Upload,
  Settings2,
  Trash2
} from 'lucide-react';

import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useSelectAllPages } from '@/hooks/use-select-all-pages';
import { useHashDialog, useHashParamDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import { Link } from '@/i18n/routing';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SelectAllBanner } from '@/components/select-all-banner';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import { PermissionGate } from '@/features/auth';
import { toast } from 'sonner';
import { ocrTemplatesApi } from '../services/ocr-templates.api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { dynamicFormsApi, type DynamicForm } from '@/features/dynamic-forms';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import { ocrTemplateQueries } from '../services/ocr-templates.queries';
import { useDeleteOcrTemplateMutation } from '../services/ocr-templates.mutations';
import { OcrTemplateCard } from './ocr-template-card';
import { OcrTemplateCompactRow } from './ocr-template-compact-row';
import { TemplateFieldsInfo } from './template-fields-info';
import { OcrTemplateFormDialog } from './ocr-template-form-dialog';
import { OcrTemplatesImportDialog } from './ocr-templates-import-dialog';
import { OcrTemplatesTableActionBar } from './ocr-templates-table-action-bar';
import { OCR_TEMPLATES_HASH } from '../constants';
import type { OcrTemplate, OcrTemplatesListParams } from '../types';

const STATUS_STYLES: Record<string, string> = {
  active:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  inactive: 'border-gray-500/30 bg-gray-500/10 text-gray-600 dark:text-gray-400'
};

export function OcrTemplatesListing() {
  const t = useTranslations('ocrTemplates');
  // Overlay open-state lives in the URL hash so overlays are deep-linkable,
  // survive refresh and close on browser Back. Destructure the stable callbacks
  // so they can go in hook dep arrays without tripping the React Compiler's
  // manual-memoization check.
  const { open: createOpen, onOpenChange: toggleCreate } = useHashDialog(
    OCR_TEMPLATES_HASH.create
  );
  const { open: importOpen, onOpenChange: toggleImport } = useHashDialog(
    OCR_TEMPLATES_HASH.import
  );
  const {
    open: editOpen,
    param: editId,
    openWith: openEdit,
    onOpenChange: closeEdit
  } = useHashParamDialog(OCR_TEMPLATES_HASH.edit);
  const {
    open: deleteOpen,
    param: deleteId,
    openWith: openDelete,
    onOpenChange: closeDelete
  } = useHashParamDialog(OCR_TEMPLATES_HASH.delete);
  const [view, setView] = useDataView('ocr-templates');

  const deleteMutation = useDeleteOcrTemplateMutation();

  // URL state
  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState(
    'name',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlActive] = useQueryState(
    'is_active',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlDynamicForm] = useQueryState(
    'dynamic_form',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlCustomFields] = useQueryState(
    'custom_fields_count',
    parseAsString.withDefault('')
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<OcrTemplate>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<OcrTemplate>[])
  );

  const dynamicFormSelect = useInfiniteSelect({
    queryKey: ['dynamic-forms', 'select'],
    queryFn: (params) => dynamicFormsApi.getAll(params),
    mapOption: useCallback(
      (form: DynamicForm) => ({
        label: form.name,
        value: String(form.id)
      }),
      []
    )
  });

  const queryParams = useMemo<OcrTemplatesListParams>(() => {
    const params: OcrTemplatesListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlSearch.length) {
      params.search = urlSearch.join(',');
    }
    if (urlActive.length === 1) {
      params.is_active = urlActive[0] === 'true';
    }
    if (urlDynamicForm.length) {
      params.dynamic_form_id = urlDynamicForm;
    }
    if (urlCustomFields) {
      const parts = String(urlCustomFields)
        .split(',')
        .map(Number)
        .filter((n) => !isNaN(n));
      if (parts.length >= 1) params.min_custom_fields = parts[0];
      if (parts.length >= 2) params.max_custom_fields = parts[1];
    }
    params.sort_by = urlSorting[0]?.id ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';
    return params;
  }, [
    urlPage,
    urlPerPage,
    urlSearch,
    urlActive,
    urlDynamicForm,
    urlCustomFields,
    urlSorting
  ]);

  const { data, isPending, isPlaceholderData } = useQuery(
    ocrTemplateQueries.list(queryParams)
  );

  const templates = useMemo(() => (data?.data ?? []) as OcrTemplate[], [data]);
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;

  // Resolve the hash param (`#edit/5` / `#delete/5`) to a loaded template.
  const editTarget = editOpen
    ? (templates.find((tpl) => String(tpl.id) === editId) ?? null)
    : null;
  const deleteTarget = deleteOpen
    ? (templates.find((tpl) => String(tpl.id) === deleteId) ?? null)
    : null;

  const columns = useMemo<ColumnDef<OcrTemplate>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('name')} />
        ),
        cell: ({ row, column }) => (
          <div className='flex items-center gap-3'>
            <div className='bg-primary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-md'>
              <FileText className='text-primary h-4 w-4' />
            </div>
            <div>
              <span className='flex items-center gap-1.5 font-medium'>
                <HighlightMatch
                  text={row.original.name}
                  query={getColumnSearchTerms(column)}
                />
                <TemplateFieldsInfo templateId={row.original.id} />
              </span>
              {row.original.description && (
                <p className='text-muted-foreground max-w-[300px] truncate text-xs'>
                  {row.original.description}
                </p>
              )}
            </div>
          </div>
        ),
        enableColumnFilter: true,
        meta: {
          label: t('searchPlaceholder'),
          variant: 'text' as const,
          placeholder: t('searchPlaceholder'),
          permission: 'ocr_templates.column.name'
        }
      },
      {
        id: 'dynamic_form',
        accessorFn: (row) => row.dynamic_form?.name,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('dynamicForm')} />
        ),
        cell: ({ row }) => (
          <span className='text-muted-foreground text-sm'>
            {row.original.dynamic_form?.name ?? '-'}
          </span>
        ),
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: t('dynamicForm'),
          variant: 'infiniteMultiSelect' as const,
          permission: 'ocr_templates.column.dynamicForm'
        }
      },
      {
        id: 'custom_fields_count',
        accessorKey: 'custom_fields_count',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('customFields')} />
        ),
        cell: ({ row }) => (
          <span className='text-muted-foreground text-sm'>
            {row.original.custom_fields_count ?? 0}
          </span>
        ),
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: t('customFields'),
          variant: 'range' as const,
          range: [0, 50] as [number, number],
          permission: 'ocr_templates.column.customFields'
        }
      },
      {
        id: 'is_active',
        accessorKey: 'is_active',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('status')} />
        ),
        cell: ({ row }) => (
          <Badge
            variant='outline'
            className={cn(
              'text-xs uppercase',
              STATUS_STYLES[row.original.is_active ? 'active' : 'inactive']
            )}
          >
            {row.original.is_active ? t('statusActive') : t('statusInactive')}
          </Badge>
        ),
        enableColumnFilter: true,
        meta: {
          label: t('status'),
          variant: 'select' as const,
          options: [
            { label: t('statusActive'), value: 'true' },
            { label: t('statusInactive'), value: 'false' }
          ],
          permission: 'ocr_templates.column.status'
        }
      },
      {
        id: 'actions',
        header: () => <div className='text-end'>{t('actions')}</div>,
        cell: ({ row }) => {
          const template = row.original;
          return (
            <div className='flex justify-end'>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' size='icon' className='h-8 w-8'>
                    <MoreHorizontal className='h-4 w-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <PermissionGate permissions={['ocr_templates.edit']}>
                    <DropdownMenuItem
                      onClick={() => openEdit(String(template.id))}
                    >
                      <Pencil className='me-2 h-4 w-4' />
                      {t('edit')}
                    </DropdownMenuItem>
                  </PermissionGate>
                  <PermissionGate permissions={['ocr_templates.edit']}>
                    <DropdownMenuItem asChild>
                      <Link
                        href={`/dashboard/ocr-templates/${template.id}/configure`}
                      >
                        <Settings2 className='me-2 h-4 w-4' />
                        {t('configure')}
                      </Link>
                    </DropdownMenuItem>
                  </PermissionGate>
                  <PermissionGate permissions={['ocr_templates.delete']}>
                    <DropdownMenuItem
                      className='text-destructive'
                      onClick={() => openDelete(String(template.id))}
                    >
                      <Trash2 className='me-2 h-4 w-4' />
                      {t('delete')}
                    </DropdownMenuItem>
                  </PermissionGate>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      }
    ],
    [t, openEdit, openDelete]
  );

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<OcrTemplate>({
    columns: filteredColumns,
    pageKey: 'ocr-templates',
    data: templates,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const selectAll = useSelectAllPages(table, pagination?.total ?? 0);

  const getBulkPayload = useCallback((): BulkPayload => {
    if (selectAll.isAllPagesSelected) {
      const { page, per_page, sort_by, sort_direction, ...filters } =
        queryParams;
      return { mode: 'all', filters };
    }
    const selectedIds = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id);
    return { mode: 'ids', ids: selectedIds };
  }, [selectAll.isAllPagesSelected, queryParams, table]);

  const [isExporting, setIsExporting] = useState(false);

  const getExportPayload = useCallback(():
    | { ids: number[] }
    | { all: true; filters: Partial<OcrTemplatesListParams> } => {
    const payload = getBulkPayload();
    if (payload.mode === 'ids' && payload.ids.length > 0) {
      return { ids: payload.ids };
    }
    const { page, per_page, sort_by, sort_direction, ...filters } = queryParams;
    return { all: true, filters };
  }, [getBulkPayload, queryParams]);

  const handleExport = async (format: 'xlsx' | 'json') => {
    setIsExporting(true);
    try {
      await ocrTemplatesApi.exportTemplates(format, getExportPayload());
    } catch {
      toast.error(t('exportError'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleBulkComplete = useCallback(() => {
    selectAll.reset();
  }, [selectAll]);

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
    } catch {
      // handled by global MutationCache
    }
    closeDelete(false);
  }

  const sortOptions = useMemo(() => [{ label: t('name'), value: 'name' }], [t]);

  const tableState = table.state;

  return (
    <div className='space-y-6'>
      {/* Toolbar */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex items-center gap-2'>
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey='ocr-templates'
          />
        </div>
        <div className='flex items-center gap-2'>
          <PermissionGate permissions={['ocr_templates.view']}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='outline' disabled={isExporting}>
                  <Download className='me-2 h-4 w-4' />
                  {isExporting ? t('exporting') : t('exportTemplates')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='start'>
                <DropdownMenuItem onClick={() => handleExport('xlsx')}>
                  {t('exportAsXlsx')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('json')}>
                  {t('exportAsJson')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </PermissionGate>
          <PermissionGate permissions={['ocr_templates.create']}>
            <Button variant='outline' onClick={() => toggleImport(true)}>
              <Upload className='me-2 h-4 w-4' />
              {t('importTemplates')}
            </Button>
          </PermissionGate>
          <PermissionGate permissions={['ocr_templates.create']}>
            <Button onClick={() => toggleCreate(true)}>
              <Plus className='me-2 h-4 w-4' />
              {t('addTemplate')}
            </Button>
          </PermissionGate>
        </div>
      </div>

      <DataTableToolbar
        table={table}
        viewFilterPermission='ocr_templates.view_filter'
        infiniteConfigs={{ dynamic_form: dynamicFormSelect }}
      />

      {/* Data */}
      {isPending ? (
        <ListingSkeleton view={view} />
      ) : templates.length === 0 && tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <FileText className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noTemplates')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {templates.map((template) => (
              <OcrTemplateCard
                key={template.id}
                template={template}
                onEdit={(template) => openEdit(String(template.id))}
                onDelete={(template) => openDelete(String(template.id))}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {templates.map((template) => (
              <OcrTemplateCompactRow
                key={template.id}
                template={template}
                onEdit={(template) => openEdit(String(template.id))}
                onDelete={(template) => openDelete(String(template.id))}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col gap-2',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <SelectAllBanner
            isAllPagesSelected={selectAll.isAllPagesSelected}
            isCurrentPageFullySelected={selectAll.isCurrentPageFullySelected}
            currentPageCount={templates.length}
            totalCount={pagination?.total ?? 0}
            pageSize={tableState.pagination.pageSize}
            onSelectAllPages={() => selectAll.setAllPagesSelected(true)}
            onClearSelection={() => selectAll.reset()}
          />
          <DataTable
            table={table}
            actionBar={
              <OcrTemplatesTableActionBar
                table={table}
                getBulkPayload={getBulkPayload}
                onComplete={handleBulkComplete}
                isAllPagesSelected={selectAll.isAllPagesSelected}
                totalCount={pagination?.total ?? 0}
              />
            }
          />
        </div>
      )}

      {/* Form dialog — one instance drives both create (#create) and edit
          (#edit/<id>); the open flag routes closing to the right hash. */}
      <OcrTemplateFormDialog
        open={createOpen || (editOpen && !!editTarget)}
        onOpenChange={(open) => {
          if (!open) (editOpen ? closeEdit : toggleCreate)(false);
        }}
        template={editOpen ? editTarget : null}
      />

      {/* Bulk import (#import) — XLSX workbook or JSON */}
      <OcrTemplatesImportDialog open={importOpen} onOpenChange={toggleImport} />

      {/* Delete confirmation */}
      <AlertDialog
        open={deleteOpen && !!deleteTarget}
        onOpenChange={closeDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirmDescription', {
                name: deleteTarget?.name ?? ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              {deleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('confirmDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
